// S-L10: LOC Draw Request Modal
import { useState } from 'react';
import { Modal, Form, InputNumber, message, Typography } from 'antd';
import { creditLineEndpoints } from '../api/creditLineEndpoints';
import { formatCurrency } from '../utils/formatters';

const { Text } = Typography;

interface LocDrawRequestModalProps {
  open: boolean;
  onClose: () => void;
  maxAmount: number; // in cents
  onSuccess: () => void;
}

export function LocDrawRequestModal({ open, onClose, maxAmount, onSuccess }: LocDrawRequestModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const amountCents = Math.round(values.amount * 100);

      setLoading(true);
      await creditLineEndpoints.createDrawRequest({ requested_amount_cents: amountCents });
      
      message.success('Draw request submitted successfully! Awaiting admin approval.');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (!error.errorFields) {
        // Not a form validation error
        message.error('Failed to submit draw request. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Request Line of Credit Draw"
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText="Submit Request"
      confirmLoading={loading}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="Draw Amount"
          name="amount"
          rules={[
            { required: true, message: 'Please enter an amount' },
            { 
              type: 'number', 
              min: 0.01, 
              message: 'Amount must be greater than $0.00' 
            },
            { 
              type: 'number', 
              max: maxAmount / 100, 
              message: `Amount cannot exceed your available credit (${formatCurrency(maxAmount)})` 
            }
          ]}
          extra={
            <Text type="secondary">
              Maximum available: {formatCurrency(maxAmount)}
            </Text>
          }
        >
          <InputNumber
            prefix="$"
            style={{ width: '100%' }}
            precision={2}
            min={0}
            max={maxAmount / 100}
            placeholder="0.00"
            autoFocus
          />
        </Form.Item>

        <Text type="secondary" style={{ fontSize: '12px' }}>
          Your draw request will be reviewed by an administrator. Once approved, the funds will be disbursed
          and added to your Outstanding Balance. Interest will begin accruing daily on the drawn amount.
        </Text>
      </Form>
    </Modal>
  );
}
